export interface Disposable {
  readonly disposed: boolean;
  dispose(): void;
}

export type DisposableLike = Disposable | (() => void);

class DisposableHandle implements Disposable {
  private _disposed = false;

  constructor(private cleanup: (() => void) | undefined) {}

  get disposed(): boolean {
    return this._disposed;
  }

  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    
    const cleanup = this.cleanup;
    this.cleanup = undefined;
    cleanup?.();
  }
}

export function createDisposable(cleanup: () => void): Disposable {
  return new DisposableHandle(cleanup);
}

export function disposedDisposable(): Disposable {
  const disposable = new DisposableHandle(undefined);
  disposable.dispose();
  return disposable;
}

export class DisposableGroup implements Disposable {
  private readonly entries: Disposable[] = [];
  private _disposed = false;

  get disposed(): boolean {
    return this._disposed;
  }

  add<T extends DisposableLike>(entry: T): T {
    const disposable: Disposable =
      typeof entry === "function"
        ? createDisposable(entry as () => void)
        : (entry as Disposable);
    if (this._disposed) {
      disposable.dispose();
    } else {
      this.entries.push(disposable);
    }
    return entry;
  }

  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;

    const errors: unknown[] = [];
    for (let index = this.entries.length - 1; index >= 0; index--) {
      try {
        this.entries[index].dispose();
      } catch (error) {
        errors.push(error);
      }
    }
    this.entries.length = 0;

    if (errors.length === 1) throw errors[0];
    if (errors.length > 1) {
      throw new AggregateError(errors, "Multiple disposal callbacks failed");
    }
  }
}
